import React, { useState } from "react";
import { Sparkles, Users, Award, Briefcase } from "lucide-react";
import { COMPANY_CUSTOM_LOGOS } from "./CompanyLogos";

// Top 50 Companies data with domain for Google Icons service
export const TOP_50_COMPANIES = [
  // --- Row 1 (Tech Giants & Product Leaders: 1-25) ---
  { id: "google", name: "Google", domain: "google.com", category: "Tech / Cloud" },
  { id: "microsoft", name: "Microsoft", domain: "microsoft.com", category: "Tech / Enterprise" },
  { id: "apple", name: "Apple", domain: "apple.com", category: "Consumer Tech" },
  { id: "amazon", name: "Amazon", domain: "amazon.com", category: "Cloud / E-Commerce" },
  { id: "meta", name: "Meta", domain: "meta.com", category: "Social / AI" },
  { id: "netflix", name: "Netflix", domain: "netflix.com", category: "Entertainment" },
  { id: "nvidia", name: "NVIDIA", domain: "nvidia.com", category: "AI & Chips" },
  { id: "adobe", name: "Adobe", domain: "adobe.com", category: "Creative Software" },
  { id: "salesforce", name: "Salesforce", domain: "salesforce.com", category: "Enterprise Cloud" },
  { id: "oracle", name: "Oracle", domain: "oracle.com", category: "Database & Cloud" },
  { id: "ibm", name: "IBM", domain: "ibm.com", category: "IT & Systems" },
  { id: "intel", name: "Intel", domain: "intel.com", category: "Semiconductors" },
  { id: "cisco", name: "Cisco", domain: "cisco.com", category: "Networking" },
  { id: "uber", name: "Uber", domain: "uber.com", category: "Tech / Mobility" },
  { id: "spotify", name: "Spotify", domain: "spotify.com", category: "Audio Streaming" },
  { id: "airbnb", name: "Airbnb", domain: "airbnb.com", category: "Travel Tech" },
  { id: "stripe", name: "Stripe", domain: "stripe.com", category: "Global Fintech" },
  { id: "linkedin", name: "LinkedIn", domain: "linkedin.com", category: "Professional Network" },
  { id: "atlassian", name: "Atlassian", domain: "atlassian.com", category: "Productivity" },
  { id: "paypal", name: "PayPal", domain: "paypal.com", category: "Fintech / Payments" },
  { id: "qualcomm", name: "Qualcomm", domain: "qualcomm.com", category: "Wireless & 5G" },
  { id: "zoom", name: "Zoom", domain: "zoom.us", category: "Video / SaaS" },
  { id: "slack", name: "Slack", domain: "slack.com", category: "Workplace / AI" },
  { id: "shopify", name: "Shopify", domain: "shopify.com", category: "E-Commerce SaaS" },
  { id: "tesla", name: "Tesla", domain: "tesla.com", category: "Automotive / Tech" },

  // --- Row 2 (Consulting, Finance, Global Leaders & Indian Titans: 26-50) ---
  { id: "mckinsey", name: "McKinsey & Co", domain: "mckinsey.com", category: "Strategy Consulting" },
  { id: "bcg", name: "BCG", domain: "bcg.com", category: "Management Consulting" },
  { id: "bain", name: "Bain & Company", domain: "bain.com", category: "Strategy Consulting" },
  { id: "goldman", name: "Goldman Sachs", domain: "goldmansachs.com", category: "Investment Banking" },
  { id: "jpmorgan", name: "JPMorgan Chase", domain: "jpmorganchase.com", category: "Global Finance" },
  { id: "morganstanley", name: "Morgan Stanley", domain: "morganstanley.com", category: "Wealth & Banking" },
  { id: "deloitte", name: "Deloitte", domain: "deloitte.com", category: "Advisory & Audit" },
  { id: "pwc", name: "PwC", domain: "pwc.com", category: "Professional Services" },
  { id: "ey", name: "EY", domain: "ey.com", category: "Assurance & Strategy" },
  { id: "kpmg", name: "KPMG", domain: "kpmg.com", category: "Audit & Tax" },
  { id: "accenture", name: "Accenture", domain: "accenture.com", category: "Consulting & Tech" },
  { id: "tcs", name: "Tata (TCS)", domain: "tcs.com", category: "Global IT Leader" },
  { id: "infosys", name: "Infosys", domain: "infosys.com", category: "Digital Services" },
  { id: "wipro", name: "Wipro", domain: "wipro.com", category: "IT & Automation" },
  { id: "reliance", name: "Reliance (Jio)", domain: "jio.com", category: "Telecom / Tech" },
  { id: "flipkart", name: "Flipkart", domain: "flipkart.com", category: "E-Commerce" },
  { id: "swiggy", name: "Swiggy", domain: "swiggy.com", category: "Consumer Tech" },
  { id: "zomato", name: "Zomato", domain: "zomato.com", category: "Food Tech" },
  { id: "razorpay", name: "Razorpay", domain: "razorpay.com", category: "Fintech" },
  { id: "samsung", name: "Samsung", domain: "samsung.com", category: "Global Electronics" },
  { id: "sony", name: "Sony", domain: "sony.com", category: "Tech & Media" },
  { id: "siemens", name: "Siemens", domain: "siemens.com", category: "Industrial Tech" },
  { id: "bmw", name: "BMW", domain: "bmw.com", category: "Automotive" },
  { id: "unilever", name: "Unilever", domain: "unilever.com", category: "Global FMCG" },
  { id: "barclays", name: "Barclays", domain: "barclays.com", category: "Global Banking" },
];

const ROW_1 = TOP_50_COMPANIES.slice(0, 25);
const ROW_2 = TOP_50_COMPANIES.slice(25, 50);

function CompanyCard({ company }) {
  const [imgError, setImgError] = useState(false);
  const CustomLogo = COMPANY_CUSTOM_LOGOS[company.id];

  // Google Icons API endpoint (128px high-resolution favicon)
  const primaryGoogleIconUrl = `https://www.google.com/s2/favicons?domain=${company.domain}&sz=128`;
  const secondaryGoogleIconUrl = `https://t1.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${company.domain}&size=128`;

  return (
    <div
      className="group relative flex h-14 sm:h-16 items-center gap-3 rounded-2xl border border-slate-200/90 bg-white/95 px-4 sm:px-5 shadow-2xs backdrop-blur-sm transition-all duration-300 hover:border-[#0B5D4B]/40 hover:bg-white hover:shadow-md hover:scale-[1.03] cursor-default select-none shrink-0"
      title={`${company.name} — ${company.category}`}
    >
      {/* Brand Icon container */}
      <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-xl bg-slate-50 border border-slate-100 p-1.5 shadow-2xs transition-transform duration-300 group-hover:scale-110">
        {CustomLogo ? (
          <CustomLogo className="h-6 w-6 sm:h-7 sm:w-7" />
        ) : !imgError ? (
          <img
            src={primaryGoogleIconUrl}
            alt={`${company.name} logo`}
            loading="lazy"
            className="h-6 w-6 sm:h-7 sm:w-7 object-contain rounded-sm"
            onError={(e) => {
              if (e.target.src !== secondaryGoogleIconUrl) {
                e.target.src = secondaryGoogleIconUrl;
              } else {
                setImgError(true);
              }
            }}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center rounded bg-[#0B5D4B]/10 text-xs font-bold text-[#0B5D4B]">
            {company.name.slice(0, 2).toUpperCase()}
          </div>
        )}
      </div>

      {/* Company Name and Category */}
      <div className="flex flex-col text-left">
        <span className="text-xs sm:text-sm font-bold text-slate-900 tracking-tight whitespace-nowrap transition-colors duration-200 group-hover:text-[#0B5D4B]">
          {company.name}
        </span>
        <span className="text-[10px] font-medium text-slate-400 whitespace-nowrap">
          {company.category}
        </span>
      </div>
    </div>
  );
}

export default function CompanyMentorsMarquee() {
  return (
    <section className="relative w-full overflow-hidden bg-gradient-to-b from-slate-50/70 via-white to-slate-50/90 py-14 sm:py-20 border-y border-slate-200/80">
      
      {/* Background Ambient Glow */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 left-1/4 h-72 w-72 rounded-full bg-[#0B5D4B]/5 blur-3xl" />
        <div className="absolute -bottom-24 right-1/4 h-72 w-72 rounded-full bg-[#C89B3C]/5 blur-3xl" />
      </div>

      {/* Section Header */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center mb-10 sm:mb-14">
        <div className="mb-3.5 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-[#0B5D4B] shadow-2xs backdrop-blur">
          <Sparkles size={14} className="text-[#C89B3C]" />
          <span>Industry Mentor Network</span>
        </div>

        <h2 className="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
          We Have Mentors from Top Companies
        </h2>

        <p className="mx-auto mt-4 max-w-3xl text-sm sm:text-base leading-relaxed text-slate-600">
          Gain real-world perspective, mock interview practice, and strategic career guidance from
          working leaders and alumni across <strong className="font-semibold text-slate-900">50+ top global and Indian enterprises</strong>.
        </p>

        {/* Quick Highlights Pills */}
        <div className="mt-5 flex flex-wrap items-center justify-center gap-2.5 sm:gap-4 text-xs font-semibold text-slate-500">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100/80 px-3 py-1">
            <Users size={13} className="text-[#0B5D4B]" /> 1-on-1 Profile Strategy
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100/80 px-3 py-1">
            <Briefcase size={13} className="text-[#C89B3C]" /> Tech, Consulting & Finance
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100/80 px-3 py-1">
            <Award size={13} className="text-[#0B5D4B]" /> Certified Industry Veterans
          </span>
        </div>
      </div>

      {/* Full-Width Dual Marquee Container */}
      <div className="relative w-full space-y-4 sm:space-y-5 overflow-hidden">
        
        {/* Left & Right Smooth Edge Fade Masks */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 w-16 sm:w-36 bg-gradient-to-r from-slate-50 via-slate-50/80 to-transparent" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 w-16 sm:w-36 bg-gradient-to-l from-slate-50 via-slate-50/80 to-transparent" />

        {/* Row 1: Moving Left (25 Companies duplicated for seamless infinite loop) */}
        <div className="flex overflow-hidden">
          <div className="animate-marquee-left flex gap-3.5 sm:gap-4.5 pr-3.5 sm:pr-4.5">
            {[...ROW_1, ...ROW_1].map((company, index) => (
              <CompanyCard key={`row1-${company.id}-${index}`} company={company} />
            ))}
          </div>
        </div>

        {/* Row 2: Moving Right (25 Companies duplicated for seamless infinite loop) */}
        <div className="flex overflow-hidden">
          <div className="animate-marquee-right flex gap-3.5 sm:gap-4.5 pr-3.5 sm:pr-4.5">
            {[...ROW_2, ...ROW_2].map((company, index) => (
              <CompanyCard key={`row2-${company.id}-${index}`} company={company} />
            ))}
          </div>
        </div>
      </div>

      {/* Subtle Bottom Trust Caption */}
      <div className="mt-8 text-center">
        <p className="text-xs font-medium text-slate-400">
          Hover over any company to pause • Continuous guidance from Class 9 to corporate placements
        </p>
      </div>

    </section>
  );
}
